import { useEffect, useRef, useState } from "react";

export default function FindUs() {
  const sectionRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt");
    if (honeypot && String(honeypot).trim()) {
      setStatus("success");
      form.reset();
      return;
    }

    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== "website_alt") data[key] = String(value);
    });

    setStatus("submitting");
    setErrorMsg("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9m06b68mbnljin59iag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (!response.ok || (parsed && parsed.code !== "OK")) {
        if (serverMsg.toLowerCase().includes("spam")) {
          setStatus("error");
          setErrorMsg("Submission failed. Please try again.");
        } else {
          setStatus("error");
          setErrorMsg(serverMsg || "Something went wrong. Please try again.");
        }
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again later.");
    }
  };

  return (
    <section id="find-us" ref={sectionRef} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal-on-scroll">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 rounded-full">
            Visit Us
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
            Find Your <span className="text-primary-600">Coffee Fix</span>
          </h2>
          <p className="text-foreground-500 text-sm md:text-base">
            We&apos;re parked and ready to serve you the best cup in town. Come find us!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="reveal-on-scroll space-y-6">
            <div className="rounded-2xl overflow-hidden h-64 md:h-80 border border-background-200 relative group">
              <iframe
                title="BB COFFEE Location at Pusat Perniagaan Bukit Raja, Shah Alam"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.1234567890123!2d101.5185!3d3.0738!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM4KwMDQnMjUuNiJOIDEwMcKwMzEnMDYuNiJF!5e0!3m2!1sen!2smy!4v1700000000000!5m2!1sen!2smy"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href="https://maps.app.goo.gl/vAAHugVGyAW8KCC78"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 left-3 right-3 bg-background-50/95 hover:bg-background-50 text-foreground-900 text-xs font-semibold py-2.5 px-4 rounded-xl text-center transition-all duration-300 opacity-0 group-hover:opacity-100 shadow-lg border border-background-200 cursor-pointer inline-flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-primary-600" />
                </div>
                Open in Google Maps
              </a>
            </div>
            <a
              href="https://maps.app.goo.gl/vAAHugVGyAW8KCC78"
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden block text-center text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              View exact location on Google Maps
            </a>

            <div className="bg-background-100 rounded-2xl p-6 border border-background-200/50">
              <h3 className="font-heading font-semibold text-foreground-900 text-base md:text-lg mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-primary-600" />
                </div>
                Visit Us
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">BB COFFEE Truck</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      Pusat Perniagaan Bukit Raja, Jalan Keluli AP7/AP, Seksyen 7, 40000 Shah Alam, Selangor (Infront of HARRAZ boutique)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Opening Hours</p>
                    <p className="text-xs text-foreground-500">Mon - Sat: 10:00 AM - 9:00 PM</p>
                    <p className="text-xs text-foreground-400">Sun: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="contact" className="reveal-on-scroll">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/50">
              <h3 className="font-heading font-semibold text-foreground-900 text-base md:text-lg mb-1">Get In Touch</h3>
              <p className="text-sm text-foreground-500 mb-6">
                Have a question or want to book us for an event? Drop us a message!
              </p>

              {status === "success" && (
                <div className="mb-6 p-4 rounded-xl bg-primary-50 border border-primary-200 text-sm text-primary-700 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-lg" />
                  </div>
                  Thanks for reaching out! We will get back to you soon.
                </div>
              )}
              {status === "error" && errorMsg && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-error-warning-line text-lg" />
                  </div>
                  {errorMsg}
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form-anti-spam" />
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">Your Name</label>
                  <input id="contact-name" name="name" type="text" required className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all" placeholder="Enter your name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">Email Address</label>
                  <input id="contact-email" name="email" type="email" required className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all" placeholder="your@email.com" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">Phone (Optional)</label>
                  <input id="contact-phone" name="phone" type="tel" className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all" placeholder="+60 12-345 6789" />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">Your Message</label>
                  <textarea id="contact-message" name="message" required rows={4} maxLength={500} className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all resize-none" placeholder="Tell us what you need..." />
                  <p className="text-[10px] text-foreground-400 mt-1 text-right">Max 500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-95 cursor-pointer inline-flex items-center justify-center gap-2"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}